import React, { useEffect, useMemo, useRef, useState } from 'react';
import { CardPreviewPane } from './components/CardCustomizationContent/CardPreviewPane';
import type { SimpleCardPdpModalProps as ProductDetailsPageProps, StationeryProductSelector, StationeryTemplatePDP } from './ProductDetailsPage.types';
import { enrichCardTelemetryExtraInfo, useCardTelemetry } from '@apps/card/Card.telemetry';
import { CardFormat } from './constants';
import { useStickyCustomizeButton } from './useStickyCustomizeButton';
import { useIsMobileOrTablet } from '@shared/utils/media/useMediaScreens';
import type { StationeryTemplateCategoryEnum } from '@graphql/generated';

import type { DigitalDesignTemplate } from './usePdpData';
import { usePdpData } from './usePdpData';
import { CardCustomizationPane } from './components/CardCustomizationContent/CardCustomizationPane';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import type { DraftData } from '@apps/card/routes/CardCustomizer/CardCustomizer.types';
import { DigitalCustomizationPane } from './components/DigitalCustomizationContent/DigitalCustomizationPane';
import { DigitalPreviewPane } from './components/DigitalCustomizationContent/DigitalPreviewPane';
import { templateToTitle } from '@apps/card/utils';
import { PdpLayout } from './components/PdpLayout/PdpLayout';
import { usePriceSummary } from '@apps/card/components/PriceSummary/usePriceSummary';
import { SkeletonGroup } from '@shared/components/Skeleton';
import { useCustomizeClickHandler } from './useCustomizeClickHandler';
import type { Format, MinimalRequiredTemplateDataForDraft } from '../DesignsGallery/DesignsGallery.types';
import { useCardCategoryContext } from '../CardCategoryProvider';
import { PremiumDigitalPdp } from './PremiumDigitalPdp';
import { isFoilTemplate } from '../DesignsGallery/utils';
import { wrapWithStationeryProductDetailsHealthProvider } from './ProductDetails.health';
import { PdpSeo } from './PdpSeo';
import { StationeryProductPricingContextProvider, useStationeryProductPricingContext } from '@apps/card/components/ProductUIConfig/utils/useStationeryProductPricingContext';
import { withWindow } from '@shared/utils/withWindow';
import { useQueryParamHelper } from '@shared/core/queryString';
import { useFreeShippingCountries } from './useFreeShippingCountries';
import { useFreeShippingBanner } from '@apps/card/hooks/useFreeShippingBanner';
import type { StationeryProduct } from '@shared/utils/print-price/types';
import { StationeryPrintOrderVendorEnum } from '@graphql/generated';

const FREE_DIGITAL_DUMMY_PRODUCT: StationeryProduct = {
  id: 'FREEDIGITALDUMMY',
  priceConfigId: '',
  vendor: StationeryPrintOrderVendorEnum.joy_digital,
  productType: 'digital',
  vendorBaseSKU: '',
  priceCurrency: '',
  attributes: [],
  pricing: {
    __typename: 'StationeryFlatFeePricing',
    priceInMinorUnits: 0,
  },
  variantGroups: [],
};

interface RootProps {
  eventId?: string;
  template: StationeryTemplatePDP;
  digitalDesignTemplate: DigitalDesignTemplate | null;
  templateCategory: StationeryTemplateCategoryEnum;
  themeId: string;
  designName: string;
  onCustomizeCardClickOverride?: ProductDetailsPageProps['onCustomizeCardClickOverride'];
}

export interface BasePdpImplementationProps {
  eventId?: string;
  themeId: string;
  templateCategory: StationeryTemplateCategoryEnum;
  isCreatingDraft: boolean;
  // cardFormatControl: CardFormatControl;
  productSelector: StationeryProductSelector;
  /**
   * Should be applied to the element that will determine when to show
   * the customize button now in the header.
   */
  intersectionRef: React.RefObject<HTMLButtonElement>;
  showCustomizeButtonInHeader: boolean;
  hideCustomizeButtonInCustomizationPane: boolean;
  designName: string;
  isFoil: boolean;
}
interface PaperPdpProps extends BasePdpImplementationProps {
  template: StationeryTemplatePDP;
  onPaperCustomization: (draft: DraftData) => void;
  onCustomizeClick: (template: MinimalRequiredTemplateDataForDraft, subtotalInMinorUnits?: number) => void;
}

const PaperPdp = (props: PaperPdpProps) => {
  const {
    productSelector,
    intersectionRef,
    isCreatingDraft,
    onCustomizeClick,
    onPaperCustomization,
    showCustomizeButtonInHeader,
    template,
    themeId,
    designName,
    templateCategory,
    eventId,
    isFoil,
  } = props;

  const themeJson = template.themeJSON;

  const isMobileOrTablet = useIsMobileOrTablet();

  const { total, totalInMinorUnits } = usePriceSummary({ templateCategory, priceUpTo: 'cardFront', cardConfig: template.themeJSON });
  const { activeProduct } = useStationeryProductPricingContext();
  const countriesWithFreeShipping = useFreeShippingCountries([activeProduct]);

  const handleOnCustomizeNowClick = useEventCallback(() => {
    onCustomizeClick(template, totalInMinorUnits);
  });

  return (
    <PdpLayout
      themeId={themeId}
      designName={designName}
      templateCategory={templateCategory}
      isPremium={template.premium}
      isFoil={isFoil}
      onCustomizeNowClick={handleOnCustomizeNowClick}
      isCreatingDraft={isCreatingDraft}
      productSelector={productSelector}
      subtotal={total}
      showCustomizeButtonInHeader={showCustomizeButtonInHeader}
      previewPane={() => (
        <CardPreviewPane
          eventId={eventId}
          templateId={template.id}
          isFavorite={template.isFavorite}
          themeId={template.themeId}
          templateCategory={templateCategory}
          cardFront={themeJson.card.front}
          cardShape={themeJson.customizations.shape}
          thumbnailUrl={template.thumbnailUrl!}
          cardJson={template.themeJSON}
        />
      )}
      customizationPane={() => (
        <CardCustomizationPane
          hideCustomizeNowButton={isMobileOrTablet}
          intersectionRef={intersectionRef}
          customizationConfig={themeJson}
          isCreatingDraft={isCreatingDraft}
          countriesWithFreeShipping={countriesWithFreeShipping}
          templateId={template.id}
          themeId={themeId}
          themeName={designName}
          stationeryTemplateCategory={templateCategory}
          onCardCustomizeClick={handleOnCustomizeNowClick}
          onCardCustomization={onPaperCustomization}
        />
      )}
    />
  );
};

interface DigitalPdpProps extends BasePdpImplementationProps {
  template: DigitalDesignTemplate;
  onCustomizeDigitalClick: () => void;
  disableCustomizeNowButton?: boolean;
}

const DigitalPdp = (props: DigitalPdpProps) => {
  const {
    productSelector,
    disableCustomizeNowButton,
    intersectionRef,
    isCreatingDraft,
    onCustomizeDigitalClick,
    showCustomizeButtonInHeader,
    template,
    designName,
    templateCategory,
    isFoil,
    themeId,
  } = props;

  const isMobileOrTablet = useIsMobileOrTablet();

  return (
    <PdpLayout
      themeId={themeId}
      isFoil={isFoil}
      designName={designName}
      templateCategory={templateCategory}
      onCustomizeNowClick={onCustomizeDigitalClick}
      isCreatingDraft={isCreatingDraft}
      productSelector={productSelector}
      disableCustomizeNowButton={disableCustomizeNowButton}
      showCustomizeButtonInHeader={showCustomizeButtonInHeader}
      previewPane={() => <DigitalPreviewPane digitalDesignTemplate={template} />}
      customizationPane={() => (
        <DigitalCustomizationPane
          themeId={themeId}
          category={templateCategory}
          hideCustomizeNowButton={isMobileOrTablet}
          intersectionRef={intersectionRef}
          isCreatingDraft={isCreatingDraft}
          onCustomizeClick={onCustomizeDigitalClick}
          disableCustomizeNowButton={disableCustomizeNowButton}
        />
      )}
    />
  );
};

const productTypes: Record<string, CardFormat> = {
  stationeryCard: CardFormat.paper,
  digitalCard: CardFormat.premiumDigital,
  digital: CardFormat.digital,
};

const convertProductTypeToCardFormat = (productType: string): CardFormat => {
  return productTypes[productType] ?? CardFormat.paper;
};

const convertCardFormatToProductType = (cardFormat: CardFormat): string => {
  return Object.keys(productTypes).find(key => productTypes[key] === cardFormat) ?? 'stationeryCard';
};

const Root = (props: RootProps) => {
  const { eventId, onCustomizeCardClickOverride, template, templateCategory, digitalDesignTemplate, themeId, designName } = props;

  const supportedTemplateFormats: Format = template && digitalDesignTemplate ? 'paperAndDigital' : template ? 'paper' : 'digital';

  const queryParams = useQueryParamHelper();
  const selectedFormat = (queryParams.getValueString('format') as CardFormat | undefined) ?? CardFormat.paper;

  const { pdpFormatControlClicked } = useCardTelemetry({ page: 'productPdpModal' });

  const hasBasicDigital = supportedTemplateFormats !== 'paper' && !template.premium;
  const products = useMemo(() => {
    const additionalProducts = hasBasicDigital ? [FREE_DIGITAL_DUMMY_PRODUCT] : [];
    return [...template.products, ...additionalProducts];
  }, [hasBasicDigital, template.products]);

  const [selectedProductId, setSelectedProductId] = useState(() => {
    const productType = convertCardFormatToProductType(selectedFormat);
    return products.find(p => p.productType === productType)?.id ?? products[0]?.id;
  });
  const selectedProductObject = products.find(p => p.id === selectedProductId);

  if (!selectedProductObject) {
    throw new Error('No product pricing found.');
  }

  const cardFormat = convertProductTypeToCardFormat(selectedProductObject.productType);

  const [storedTemplate, setStoredTemplate] = useState<StationeryTemplatePDP | null>(template);

  const handleOnPaperCustomization = useEventCallback((draft: DraftData) => {
    setStoredTemplate(prev =>
      prev
        ? {
            ...prev,
            themeJSON: draft.config,
          }
        : null,
    );
  });

  const currentProduct = products.find(p => p.id === selectedProductId);

  const { isCreatingDraft, onCustomizeClick } = useCustomizeClickHandler({
    currentCategory: templateCategory,
    eventId,
    onCustomizeCardClickOverride,
    supportedFormats: supportedTemplateFormats,
    currentProduct: currentProduct,
  });

  const handleOnCustomizeDigitalClick = useEventCallback(() => {
    if (digitalDesignTemplate) {
      onCustomizeClick('digital', digitalDesignTemplate, supportedTemplateFormats);
    }
  }, [template, digitalDesignTemplate]);

  const isMobileOrTablet = useIsMobileOrTablet();
  // Will be attached to the `CustomizeNowButton` in the customization pane
  // Used to determine when to show the button in the header
  const intersectionRef = useRef<HTMLButtonElement>(null);
  const shouldShowCustomizeButtonInHeader = useStickyCustomizeButton(intersectionRef, !isMobileOrTablet);

  const handleSetProductID = useEventCallback((newProductId: string) => {
    setSelectedProductId(newProductId);
    withWindow(global => {
      global.window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    });
    pdpFormatControlClicked({ themeId, format: newProductId });
  });

  const productSelector: StationeryProductSelector = {
    onChange: handleSetProductID,
    cardFormat: cardFormat,
    productId: selectedProductId,
  };

  const basePdpProps: BasePdpImplementationProps = {
    productSelector,
    designName,
    intersectionRef,
    isCreatingDraft,
    showCustomizeButtonInHeader: shouldShowCustomizeButtonInHeader,
    hideCustomizeButtonInCustomizationPane: isMobileOrTablet,
    templateCategory,
    themeId,
    isFoil: isFoilTemplate(template),
  };

  return (
    <StationeryProductPricingContextProvider
      products={products}
      activeProductId={selectedProductId}
    >
      {cardFormat === CardFormat.paper && storedTemplate && (
        <PaperPdp
          eventId={eventId}
          template={storedTemplate}
          onPaperCustomization={handleOnPaperCustomization}
          onCustomizeClick={() => onCustomizeClick('paper', storedTemplate, supportedTemplateFormats)}
          {...basePdpProps}
        />
      )}

      {cardFormat === CardFormat.premiumDigital && storedTemplate && (
        <PremiumDigitalPdp
          eventId={eventId}
          template={storedTemplate}
          onCustomizeClick={() => onCustomizeClick('premiumDigital', storedTemplate, supportedTemplateFormats)}
          {...basePdpProps}
        />
      )}

      {cardFormat === CardFormat.digital && digitalDesignTemplate && storedTemplate && (
        <DigitalPdp
          eventId={eventId}
          template={digitalDesignTemplate}
          onCustomizeDigitalClick={handleOnCustomizeDigitalClick}
          {...basePdpProps}
        />
      )}
    </StationeryProductPricingContextProvider>
  );
};

export const ProductDetailsPage = wrapWithStationeryProductDetailsHealthProvider((props: ProductDetailsPageProps) => {
  const { currentCategory } = useCardCategoryContext();
  const { themeId, templateId } = props;
  const { template, digitalTemplate, loading } = usePdpData({
    themeId,
    category: currentCategory,
    templateId,
    enableRedirectIfInvalid: true,
    enableUpdateUrlToIncludeTemplateId: true,
  });

  enrichCardTelemetryExtraInfo({ stationeryTemplateCategory: currentCategory });

  useEffect(() => {
    enrichCardTelemetryExtraInfo({ stationeryTemplateCategory: null });
  }, []);

  // Extracting design name here for consistency between pdps
  const designName = (template && templateToTitle(template)) || digitalTemplate?.name || '';

  useFreeShippingBanner(template?.products ?? []);

  return (
    <>
      <PdpSeo
        themeId={themeId}
        currentCategory={currentCategory}
      />
      <SkeletonGroup
        isReady={!loading && !!template?.products.length}
        placeholder={<PdpLayout.Skeleton />}
        width="100%"
      >
        {template ? (
          <Root
            key={templateId}
            template={template}
            digitalDesignTemplate={digitalTemplate}
            designName={designName}
            templateCategory={currentCategory}
            {...props}
          />
        ) : null}
      </SkeletonGroup>
    </>
  );
});
